import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { Modal, Typography } from '@getsynapse/design-system';
import unarchiveIcon from 'assets/icons/unarchive.svg';
import { updateRequest } from 'state/ActiveRequest/activeRequestSlice';
import {
  getRequests,
  requestsSelectors,
  resetRequests,
} from 'state/normalized/requests/requestsSlice';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import { getRequestPermissions } from 'state/normalized/requestPermissions/requestPermissionsSlice';

type UnarchiveRequestModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  requestId: string;
  sidePanel?: boolean;
};

const UnarchiveRequestModal = ({
  isOpen,
  setIsOpen,
  requestId,
  sidePanel = false,
}: UnarchiveRequestModalProps) => {
  const dispatch = useDispatch();
  const filterParams = useSelector(requestsSelectors.selectFilterParameter);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleUnarchiveRequest = async () => {
    handleClose();
    try {
      await dispatch(
        updateRequest({
          id: requestId,
          changes: { isArchived: false },
          isArchiving: true,
        })
      );
      await dispatch(getRequestPermissions({ requestIds: [requestId] }));
      if (sidePanel) {
        dispatch(resetRequests());
        dispatch(getRequests({ ...filterParams }));
      }
    } catch (error) {
      dispatch(
        showSnackbarNotification({
          notificationVariant: 'error',
          notificationTitle: intl.get(
            'REQUEST_PAGE.TOP_BAR.UNARCHIVE_REQUEST_MODAL.UNARCHIVE_ERROR_TITLE'
          ),
          notificationMessage: intl.get(
            'REQUEST_PAGE.TOP_BAR.UNARCHIVE_REQUEST_MODAL.UNARCHIVE_ERROR_MESSAGE'
          ),
          autoHide: false,
        })
      );
    }
  };

  return (
    <Modal
      aria-label={intl.get(
        'REQUEST_PAGE.TOP_BAR.UNARCHIVE_REQUEST_MODAL.TITLE'
      )}
      closeModal={handleClose}
      isOpen={isOpen}
      title={intl.get('REQUEST_PAGE.TOP_BAR.UNARCHIVE_REQUEST_MODAL.TITLE')}
      size='small'
      data-testid='uarchive-request-modal'
      titleIcon={{
        src: unarchiveIcon,
        className: 'text-primary-dark h-5 w-5',
      }}
      actionButtons={[
        {
          children: intl.get(
            'REQUEST_PAGE.TOP_BAR.UNARCHIVE_REQUEST_MODAL.UNARCHIVE_BUTTON'
          ),
          onClick: handleUnarchiveRequest,
          variant: 'primary',
          'data-cy': 'modal-unarchive-request-button',
          className: 'h-10',
        },
        {
          children: intl.get('CANCEL'),
          onClick: handleClose,
          variant: 'tertiary',
          'data-cy': 'cancel-unarchive-request-button',
          className: 'h-10',
        },
      ]}
      childrenClassName='p-0'
      overlayClassName='z-high'
    >
      <Typography className='mb-6'>
        {intl.get('REQUEST_PAGE.TOP_BAR.UNARCHIVE_REQUEST_MODAL.BODY')}
      </Typography>
    </Modal>
  );
};

export default UnarchiveRequestModal;
